import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription,
  AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import LocationPicker from '../components/LocationPicker';
import { SavedAddressesBlock } from '../components/SavedAddressesBlock';
import WegooLoader from '../components/WegooLoader';
import { trackPixel } from '../utils/metaPixel';
import { 
  Banknote, CreditCard, Smartphone, Apple, 
  MapPin, User, Phone, Waves, Loader2, Map, Lock, AlertCircle, Ticket, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const { t, language } = useLanguage();
  const { items, subtotal, deliveryFee, total, isBeachMode, clearCart, setIsBeachMode, restaurantId, setDeliveryFee, distanceKm, setDistanceKm } = useCart();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(true); // Always show map
  const [gpsCoordinates, setGpsCoordinates] = useState('');
  const [step, setStep] = useState(1); // 1: Details, 2: Location, 3: Payment
  const [orderId, setOrderId] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [deliveryUnavailable, setDeliveryUnavailable] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState(null);
  const [blockReason, setBlockReason] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    delivery_location: '',
    location_type: 'hotel',
    hotel_name: '',
    room_number: '',
    payment_method: 'card', // Default to card
    notes: '',
    nif_food: '',
    nif_delivery: ''
  });

  // Update form when user data is loaded
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: prev.customer_name || user.name || '',
        phone: prev.phone || user.phone || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (authLoading) {
      return;
    }
    
    // Require authentication - redirect to login if not authenticated
    if (!user) {
      toast.error(language === 'pt' ? 'Faca login para continuar com o pedido' : 'Please login to continue with your order');
      navigate('/login?redirect=/checkout');
      return;
    }
    
    if (items.length === 0 && !orderId) {
      navigate('/restaurants');
    }
    
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    
    fetchSettings();
  }, [items, navigate, orderId, token, user, language, authLoading]);

  useEffect(() => {
    if (formData.location_type === 'beach') {
      setIsBeachMode(true);
    }
  }, [formData.location_type, setIsBeachMode]);

  // Distance-based delivery fee — recalculates whenever the customer drops a
  // new pin on the map or switches restaurant in the cart.
  useEffect(() => {
    if (!gpsCoordinates) return;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/delivery/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            destination_gps: gpsCoordinates,
            restaurant_id: restaurantId || null,
          }),
          signal: controller.signal,
        });
        if (res.ok) {
          const data = await res.json();
          setDistanceKm(data.distance_km ?? null);
          if (data.available === false) {
            setDeliveryUnavailable(true);
            setUnavailableReason(data.reason || null);
          } else if (typeof data.delivery_fee === 'number') {
            setDeliveryUnavailable(false);
            setUnavailableReason(null);
            setDeliveryFee(data.delivery_fee);
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Delivery quote error:', e);
      }
    })();
    return () => controller.abort();
  }, [gpsCoordinates, restaurantId, setDeliveryFee, setDistanceKm]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Cancel the in-progress checkout (no order created yet): empty the cart and
  // return to the restaurant list.
  const handleCancelOrder = () => {
    clearCart();
    toast.success(language === 'pt' ? 'Pedido cancelado' : 'Order cancelled');
    navigate('/restaurants');
  };

  // Step 1: Validate details and move to location
  const handleNextToLocation = () => {
    if (!formData.customer_name || !formData.phone) {
      toast.error(language === 'pt' ? 'Preencha o nome e telefone' : 'Fill in name and phone');
      return;
    }
    setStep(2);
  };

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/promotions/apply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode.trim(), subtotal, restaurant_id: items[0]?.restaurant_id || null })
      });
      if (res.ok) {
        const data = await res.json();
        setAppliedCoupon(data);
        toast.success(data.message);
      } else {
        const err = await res.json().catch(() => ({}));
        const detail = typeof err.detail === 'string' ? err.detail : null;
        toast.error(detail || (language === 'pt' ? 'Não foi possível aplicar este cupão' : 'Could not apply this coupon'));
      }
    } catch (err) {
      toast.error(language === 'pt' ? 'Erro ao aplicar cupao' : 'Error applying coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const discount = appliedCoupon?.discount_amount || 0;
  const finalTotal = Math.max(0, total - discount);

  // Meta Pixel: InitiateCheckout (once per checkout visit)
  const initiateTrackedRef = React.useRef(false);
  useEffect(() => {
    if (!initiateTrackedRef.current && items.length > 0) {
      initiateTrackedRef.current = true;
      trackPixel('InitiateCheckout', {
        content_ids: items.map(i => i.item_id),
        num_items: items.reduce((s, i) => s + i.quantity, 0),
        value: total,
        currency: 'EUR',
      });
    }
  }, [items, total]);

  // Step 2: Validate location and move to payment - MAP IS REQUIRED
  const handleNextToPayment = () => {
    // GPS coordinates from map are REQUIRED for all deliveries
    if (!gpsCoordinates) {
      toast.error(language === 'pt' ? 'Selecione a localização no mapa' : 'Select location on map');
      return;
    }
    // Written address is also required
    if (!formData.delivery_location) {
      toast.error(language === 'pt' ? 'Preencha o endereço de entrega' : 'Fill in delivery address');
      return;
    }
    if (deliveryUnavailable) {
      toast.error(unavailableReason === 'outside_zone'
        ? (language === 'pt' ? 'Localização fora da zona de entrega WEGOO' : 'Location outside the WEGOO delivery zone')
        : (language === 'pt' ? 'Entrega não disponível na tua área' : 'Delivery not available in your area'));
      return;
    }
    setStep(3);
  };

  // Step 3: Process payment
  const handlePayment = async () => {
    setLoading(true);

    try {
      // First create the order
      const firstRestaurantId = items[0]?.restaurant_id || null;
      const orderData = {
        items: items.map(item => ({
          item_id: item.item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          restaurant_id: item.restaurant_id,
          restaurant_name: item.restaurant_name
        })),
        restaurant_id: firstRestaurantId,
        customer_name: formData.customer_name,
        phone: formData.phone,
        delivery_location: formData.delivery_location,
        location_type: formData.location_type,
        hotel_name: formData.hotel_name,
        room_number: formData.room_number,
        payment_method: formData.payment_method,
        notes: formData.notes,
        nif_food: formData.nif_food || null,
        nif_delivery: formData.nif_delivery || null,
        is_beach_mode: isBeachMode,
        gps_coordinates: gpsCoordinates,
        delivery_fee: deliveryFee,
        subtotal: subtotal,
        total: finalTotal,
        discount: discount,
        promo_id: appliedCoupon?.promo_id || null,
        promo_code: appliedCoupon?.code || null,
        payment_status: 'processing'
      };

      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Create order first
      const orderResponse = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(orderData)
      });

      if (!orderResponse.ok) {
        const errData = await orderResponse.json().catch(() => ({}));
        const detail = typeof errData.detail === 'string' ? errData.detail : null;
        // Closed / availability reasons get a clear dialog, not a scary error toast.
        setBlockReason(detail || (language === 'pt' ? `Não foi possível criar o pedido (${orderResponse.status})` : `Could not create the order (${orderResponse.status})`));
        setLoading(false);
        return;
      }

      const order = await orderResponse.json();
      setOrderId(order.order_id);

      // Record coupon use if applied
      if (appliedCoupon?.promo_id) {
        try {
          await fetch(`${API_URL}/api/promotions/use`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ promo_id: appliedCoupon.promo_id, order_id: order.order_id })
          });
        } catch (e) { console.error('Coupon use record error:', e); }
      }

      // If paying with card, redirect to Stripe
      if (formData.payment_method === 'card' || formData.payment_method === 'apple_pay') {
        const checkoutResponse = await fetch(`${API_URL}/api/payments/create-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            order_id: order.order_id,
            amount: finalTotal,
            order_type: 'food'
          })
        });

        if (!checkoutResponse.ok) {
          const errData = await checkoutResponse.json().catch(() => ({}));
          throw new Error(errData.detail || `Erro ao criar sessão de pagamento (${checkoutResponse.status})`);
        }

        const checkoutData = await checkoutResponse.json();
        
        // Clear cart before redirect
        clearCart();
        
        // Redirect to Stripe Checkout
        window.location.href = checkoutData.url;
        return;
      }

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(
        error?.message || (language === 'pt' ? 'Erro ao processar pedido' : 'Error processing order')
      );
    } finally {
      setLoading(false);
    }
  };

  // Payment methods
  const paymentMethods = [
    { 
      id: 'card', 
      label: language === 'pt' ? 'Cartão (Visa/Mastercard)' : 'Card (Visa/Mastercard)', 
      icon: CreditCard, 
      description: language === 'pt' ? 'Pagamento seguro via Stripe' : 'Secure payment via Stripe',
      online: true
    },
    { 
      id: 'apple_pay', 
      label: 'Apple Pay', 
      icon: Apple, 
      description: language === 'pt' ? 'Pagamento rápido' : 'Quick payment',
      online: true
    },
  ];

  const locationTypes = [
    { id: 'hotel', label: language === 'pt' ? 'Hotel' : 'Hotel' },
    { id: 'apartment', label: language === 'pt' ? 'Apartamento' : 'Apartment' },
    { id: 'beach', label: language === 'pt' ? 'Praia' : 'Beach' },
    { id: 'marina', label: 'Marina' },
    { id: 'residential', label: language === 'pt' ? 'Residencial' : 'Residential' },
  ];

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B35] mx-auto mb-4"></div>
          <p className="text-[#9FA2B4]">{language === 'pt' ? 'A verificar sessão...' : 'Checking session...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8">
      {/* Clear, friendly reason dialog (closed / unavailable) instead of a raw error */}
      <AlertDialog open={!!blockReason} onOpenChange={(o) => { if (!o) setBlockReason(null); }}>
        <AlertDialogContent data-testid="checkout-blocked-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#004E64]">
              <AlertCircle size={20} className="text-[#FF6B35]" />
              {language === 'pt' ? 'Não foi possível concluir' : 'Could not complete'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#004E64]/80 text-sm leading-relaxed" data-testid="checkout-blocked-reason">
              {blockReason}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setBlockReason(null)}
              className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
              data-testid="checkout-blocked-ok"
            >
              {language === 'pt' ? 'Entendi' : 'Got it'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {loading && step === 3 && (
        <WegooLoader
          fullscreen
          label={language === 'pt' ? 'A enviar o teu pedido...' : 'Sending your order...'}
        />
      )}
      <div className="container mx-auto px-4">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#FF6B35]' : 'text-[#9FA2B4]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-[#FF6B35] text-white' : 'bg-gray-200'}`}>1</div>
              <span className="hidden sm:inline">{language === 'pt' ? 'Dados' : 'Details'}</span>
            </div>
            <div className={`w-12 h-1 ${step >= 2 ? 'bg-[#FF6B35]' : 'bg-gray-200'}`} />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#FF6B35]' : 'text-[#9FA2B4]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-[#FF6B35] text-white' : 'bg-gray-200'}`}>2</div>
              <span className="hidden sm:inline">{language === 'pt' ? 'Localização' : 'Location'}</span>
            </div>
            <div className={`w-12 h-1 ${step >= 3 ? 'bg-[#FF6B35]' : 'bg-gray-200'}`} />
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-[#FF6B35]' : 'text-[#9FA2B4]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-[#FF6B35] text-white' : 'bg-gray-200'}`}>3</div>
              <span className="hidden sm:inline">{language === 'pt' ? 'Pagamento' : 'Payment'}</span>
            </div>
          </div>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-[#004E64] mb-8 text-center" data-testid="checkout-title">
          {step === 1
            ? (language === 'pt' ? 'Informações de Contato' : 'Contact Information')
            : step === 2
            ? (language === 'pt' ? 'Local de Entrega' : 'Delivery Location')
            : (language === 'pt' ? 'Pagamento' : 'Payment')}
        </h1>

        {/* Cancel the whole checkout (no order exists yet) */}
        <div className="flex justify-center mb-8 -mt-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                className="text-[#9FA2B4] hover:text-red-600 hover:bg-red-50 rounded-full"
                data-testid="cancel-order-button"
              >
                <X size={16} className="mr-1" />
                {language === 'pt' ? 'Cancelar pedido' : 'Cancel order'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="cancel-order-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {language === 'pt' ? 'Cancelar este pedido?' : 'Cancel this order?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {language === 'pt'
                    ? 'Os itens do carrinho serão removidos e voltará à lista de restaurantes. Esta ação não pode ser desfeita.'
                    : 'Your cart items will be removed and you will return to the restaurant list. This action cannot be undone.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-order-dismiss">
                  {language === 'pt' ? 'Voltar' : 'Go back'}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleCancelOrder}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="cancel-order-confirm"
                >
                  {language === 'pt' ? 'Sim, cancelar' : 'Yes, cancel'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Contact Info */}
            {step === 1 && (
              <Card className="rounded-3xl border-[#004E64]/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#004E64]">
                    <User className="text-[#FF6B35]" />
                    {language === 'pt' ? 'Seus Dados' : 'Your Details'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">{language === 'pt' ? 'Nome Completo' : 'Full Name'} *</Label>
                      <Input
                        id="name"
                        value={formData.customer_name}
                        onChange={(e) => handleChange('customer_name', e.target.value)}
                        required
                        className="rounded-xl mt-1"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">{language === 'pt' ? 'Telefone' : 'Phone'} *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                        className="rounded-xl mt-1"
                        placeholder="+351 9XX XXX XXX"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes">{language === 'pt' ? 'Notas Adicionais' : 'Additional Notes'}</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      className="rounded-xl mt-1"
                      placeholder={language === 'pt' ? 'Instruções especiais...' : 'Special instructions...'}
                      data-testid="input-notes"
                    />
                  </div>

                  {/* NIF (Tax ID) — both optional, separated for legal compliance */}
                  <div className="bg-[#F4F1EA] rounded-2xl p-4 space-y-3">
                    <p className="text-sm font-semibold text-[#004E64]">
                      {language === 'pt' ? 'Faturação (opcional)' : 'Invoicing (optional)'}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nif_food" className="text-xs">
                          {language === 'pt' ? 'NIF da Comida (vai no talão do restaurante)' : 'Food NIF (printed on restaurant receipt)'}
                        </Label>
                        <Input
                          id="nif_food"
                          inputMode="numeric"
                          maxLength={9}
                          value={formData.nif_food}
                          onChange={(e) => handleChange('nif_food', e.target.value.replace(/\D/g, ''))}
                          className="rounded-xl mt-1"
                          placeholder="123456789"
                          data-testid="input-nif-food"
                        />
                      </div>
                      <div>
                        <Label htmlFor="nif_delivery" className="text-xs">
                          {language === 'pt' ? 'NIF da Entrega (fatura WEGOO)' : 'Delivery NIF (WEGOO invoice)'}
                        </Label>
                        <Input
                          id="nif_delivery"
                          inputMode="numeric"
                          maxLength={9}
                          value={formData.nif_delivery}
                          onChange={(e) => handleChange('nif_delivery', e.target.value.replace(/\D/g, ''))}
                          className="rounded-xl mt-1"
                          placeholder="123456789"
                          data-testid="input-nif-delivery"
                        />
                      </div>
                    </div>
                    <p className="text-[11px] text-[#9FA2B4] leading-snug">
                      {language === 'pt'
                        ? 'Os dois NIFs são independentes. Pode preencher só um, ambos, ou nenhum.'
                        : 'Both Tax IDs are independent. You can fill in one, both, or neither.'}
                    </p>
                  </div>
                  <Button
                    onClick={handleNextToLocation}
                    className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full py-6 text-lg font-bold"
                    data-testid="next-to-location"
                  >
                    {language === 'pt' ? 'Continuar para Localização' : 'Continue to Location'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Location */}
            {step === 2 && (
              <Card className="rounded-3xl border-[#004E64]/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#004E64]">
                    <MapPin className="text-[#FF6B35]" />
                    {language === 'pt' ? 'Onde Entregar?' : 'Where to Deliver?'}
                    {isBeachMode && (
                      <Badge className="ml-2 bg-[#2EC4B6]">
                        <Waves size={12} className="mr-1" />
                        Beach Mode
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Saved addresses chips — pick a favourite to autofill */}
                  {user && (
                    <SavedAddressesBlock
                      token={token}
                      language={language}
                      currentLocation={{
                        delivery_location: formData.delivery_location,
                        location_type: formData.location_type,
                        hotel_name: formData.hotel_name,
                        room_number: formData.room_number,
                        gps_coordinates: gpsCoordinates,
                      }}
                      onPick={(a) => {
                        setFormData((prev) => ({
                          ...prev,
                          delivery_location: a.delivery_location || '',
                          location_type: a.location_type || prev.location_type,
                          hotel_name: a.hotel_name || '',
                          room_number: a.room_number || '',
                        }));
                        if (a.gps_coordinates) setGpsCoordinates(a.gps_coordinates);
                        toast.success(language === 'pt' ? `Morada "${a.label}" aplicada` : `Address "${a.label}" applied`);
                      }}
                    />
                  )}
                  <div>
                    <Label>{language === 'pt' ? 'Tipo de Local' : 'Location Type'} *</Label>
                    <Select
                      value={formData.location_type}
                      onValueChange={(value) => handleChange('location_type', value)}
                    >
                      <SelectTrigger className="rounded-xl mt-1" data-testid="select-location-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {locationTypes.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.id === 'beach' && <Waves size={14} className="inline mr-2 text-[#2EC4B6]" />}
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="hotel">{language === 'pt' ? 'Nome do Hotel/Edifício' : 'Hotel/Building Name'}</Label>
                      <Input
                        id="hotel"
                        value={formData.hotel_name}
                        onChange={(e) => handleChange('hotel_name', e.target.value)}
                        className="rounded-xl mt-1"
                        data-testid="input-hotel"
                      />
                    </div>
                    <div>
                      <Label htmlFor="room">{language === 'pt' ? 'Quarto/Apartamento' : 'Room/Apartment'}</Label>
                      <Input
                        id="room"
                        value={formData.room_number}
                        onChange={(e) => handleChange('room_number', e.target.value)}
                        className="rounded-xl mt-1"
                        data-testid="input-room"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{language === 'pt' ? 'Endereço Escrito' : 'Written Address'}</Label>
                    <Input
                      value={formData.delivery_location}
                      onChange={(e) => handleChange('delivery_location', e.target.value)}
                      className="rounded-xl mt-1"
                      placeholder={language === 'pt' ? 'Ex: Hotel Tróia, Quarto 205' : 'Ex: Tróia Hotel, Room 205'}
                      data-testid="input-location"
                    />
                  </div>

                  {/* Map Location Picker */}
                  <div className="p-4 bg-[#2EC4B6]/10 rounded-2xl">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Map size={20} className="text-[#2EC4B6]" />
                        <Label className="text-[#004E64] font-semibold">
                          {language === 'pt' ? 'Marcar localização exata no mapa' : 'Mark exact location on map'}
                        </Label>
                      </div>
                      <Switch
                        checked={showMapPicker}
                        onCheckedChange={setShowMapPicker}
                        data-testid="toggle-map"
                      />
                    </div>
                    
                    {showMapPicker && (
                      <LocationPicker
                        value={formData.delivery_location}
                        onChange={(value) => handleChange('delivery_location', value)}
                        onCoordinatesChange={setGpsCoordinates}
                        language={language}
                        placeholder={language === 'pt' ? 'Pesquisar ou clicar no mapa...' : 'Search or click on map...'}
                      />
                    )}

                    {gpsCoordinates && (
                      <div className="mt-2 p-2 bg-white rounded-xl flex items-center gap-2">
                        <MapPin size={16} className="text-[#2EC4B6]" />
                        <span className="text-sm text-[#004E64]">GPS: {gpsCoordinates}</span>
                      </div>
                    )}

                    {deliveryUnavailable && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2" data-testid="delivery-unavailable-alert">
                        <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">
                          {unavailableReason === 'outside_zone'
                            ? (language === 'pt'
                              ? 'A tua localização está fora da zona de entrega WEGOO. Escolhe um local dentro da zona (Tróia).'
                              : 'Your location is outside the WEGOO delivery zone. Please choose a location inside the zone (Tróia).')
                            : (language === 'pt'
                              ? `Entrega não disponível na tua área${distanceKm != null ? ` (${distanceKm.toFixed(1)} km)` : ''}. Escolhe um local mais próximo.`
                              : `Delivery not available in your area${distanceKm != null ? ` (${distanceKm.toFixed(1)} km)` : ''}. Please choose a closer location.`)}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1 rounded-full py-6"
                    >
                      {language === 'pt' ? 'Voltar' : 'Back'}
                    </Button>
                    <Button
                      onClick={handleNextToPayment}
                      disabled={deliveryUnavailable}
                      className="flex-1 bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full py-6 text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="next-to-payment"
                    >
                      {language === 'pt' ? 'Continuar para Pagamento' : 'Continue to Payment'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <Card className="rounded-3xl border-[#004E64]/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#004E64]">
                    <Lock className="text-[#FF6B35]" />
                    {language === 'pt' ? 'Escolha o Método de Pagamento' : 'Choose Payment Method'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Online Payments */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-[#004E64] mb-3 flex items-center gap-2">
                      <CreditCard size={16} />
                      {language === 'pt' ? 'Pagamento Online' : 'Online Payment'}
                    </h3>
                    <RadioGroup
                      value={formData.payment_method}
                      onValueChange={(value) => handleChange('payment_method', value)}
                      className="space-y-3"
                    >
                      {paymentMethods.filter(m => m.online).map(method => (
                        <div key={method.id}>
                          <RadioGroupItem
                            value={method.id}
                            id={method.id}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={method.id}
                            className="flex items-center gap-4 p-4 rounded-2xl border-2 border-[#004E64]/10 cursor-pointer transition-all peer-data-[state=checked]:border-[#FF6B35] peer-data-[state=checked]:bg-[#FF6B35]/5 hover:bg-[#004E64]/[0.02]"
                            data-testid={`payment-${method.id}`}
                          >
                            <div className="w-12 h-12 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                              <method.icon className="text-[#FF6B35]" size={24} />
                            </div>
                            <div className="flex-1">
                              <span className="font-medium text-[#004E64]">{method.label}</span>
                              <p className="text-sm text-[#9FA2B4]">{method.description}</p>
                            </div>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>

                  <Separator className="my-6" />

                  {/* Cancellation policy — required by law (Portugal) */}
                  <div className="mt-4 p-4 bg-[#FFF7ED] rounded-xl border border-[#FF6B35]/20" data-testid="cancellation-policy">
                    <p className="text-sm font-semibold text-[#004E64] mb-2">
                      {language === 'pt' ? 'Política de Cancelamento' : 'Cancellation Policy'}
                    </p>
                    <ul className="text-xs text-[#004E64]/80 space-y-1 list-disc list-inside">
                      <li>{language === 'pt' ? 'Cancelamento gratuito enquanto o pedido estiver "Recebido".' : 'Free cancellation while order is "Received".'}</li>
                      <li>{language === 'pt' ? 'Em "Em preparação" ou "Pronto" → 50% do valor da comida.' : 'When "Preparing" or "Ready" → 50% of food value.'}</li>
                      <li>{language === 'pt' ? 'Em "Em entrega" → cobramos o valor total.' : 'Once "Out for delivery" → full charge.'}</li>
                    </ul>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => setStep(2)}
                      className="flex-1 rounded-full py-6"
                    >
                      {language === 'pt' ? 'Voltar' : 'Back'}
                    </Button>
                    <Button
                      onClick={handlePayment}
                      disabled={loading}
                      className="flex-1 bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full py-6 text-lg font-bold"
                      data-testid="confirm-payment"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" />
                          {language === 'pt' ? 'Processando...' : 'Processing...'}
                        </>
                      ) : (
                        language === 'pt' ? 'Pagar Agora' : 'Pay Now'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary - Always Visible */}
          <div className="lg:col-span-1">
            <Card className="rounded-3xl border-[#004E64]/5 sticky top-24">
              <CardHeader>
                <CardTitle className="text-[#004E64]">
                  {language === 'pt' ? 'Resumo do Pedido' : 'Order Summary'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.map(item => (
                  <div key={item.item_id} className="flex justify-between">
                    <span className="text-[#004E64]">
                      {item.quantity}x {item.name}
                    </span>
                    <span className="font-semibold text-[#004E64]">
                      €{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}

                <Separator />

                <div className="flex justify-between text-[#9FA2B4]">
                  <span>{language === 'pt' ? 'Subtotal' : 'Subtotal'}</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-[#9FA2B4]">
                  <span className="flex items-center gap-2">
                    {language === 'pt' ? 'Taxa de Entrega' : 'Delivery Fee'}
                    {distanceKm != null && (
                      <span className="text-xs text-[#9FA2B4]">({distanceKm.toFixed(1)} km)</span>
                    )}
                    {isBeachMode && <Waves size={14} className="text-[#2EC4B6]" />}
                  </span>
                  <span data-testid="checkout-delivery-fee" className={deliveryUnavailable ? 'text-red-600 font-semibold' : ''}>
                    {deliveryUnavailable
                      ? (language === 'pt' ? 'Não disponível' : 'Not available')
                      : distanceKm != null
                        ? `€${deliveryFee.toFixed(2)}`
                        : (language === 'pt' ? 'A partir de €5,00' : 'From €5.00')}
                  </span>
                </div>

                {/* Coupon Section */}
                <div className="pt-1">
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Ticket size={14} className="text-green-600" />
                        <span className="text-sm font-mono text-green-700">{appliedCoupon.code}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-green-600">-€{discount.toFixed(2)}</span>
                        <button onClick={removeCoupon} className="text-gray-400 hover:text-red-500" data-testid="remove-coupon-btn">
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder={language === 'pt' ? 'Codigo cupao' : 'Coupon code'}
                        value={couponCode}
                        onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        className="rounded-xl text-sm font-mono h-9"
                        data-testid="coupon-code-input"
                      />
                      <Button
                        size="sm"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                        className="bg-[#2EC4B6] hover:bg-[#25A89D] rounded-xl h-9 px-3 text-xs"
                        data-testid="apply-coupon-btn"
                      >
                        {couponLoading ? '...' : (language === 'pt' ? 'Aplicar' : 'Apply')}
                      </Button>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex justify-between text-lg">
                  <span className="font-bold text-[#004E64]">{language === 'pt' ? 'Total' : 'Total'}</span>
                  <span className="font-bold text-[#FF6B35]">
                    {deliveryUnavailable
                      ? '—'
                      : distanceKm != null
                        ? `€${finalTotal.toFixed(2)}`
                        : (language === 'pt' ? `A partir de €${finalTotal.toFixed(2)}` : `From €${finalTotal.toFixed(2)}`)}
                  </span>
                </div>

                {/* Delivery location preview */}
                {(formData.delivery_location || gpsCoordinates) && step >= 2 && (
                  <div className="p-3 bg-[#F4F1EA] rounded-xl">
                    <p className="text-xs text-[#9FA2B4] mb-1">{language === 'pt' ? 'Entregar em:' : 'Deliver to:'}</p>
                    <p className="text-sm text-[#004E64]">{formData.delivery_location}</p>
                    {gpsCoordinates && (
                      <p className="text-xs text-[#2EC4B6] mt-1">📍 {gpsCoordinates}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
